import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box
} from '@mui/material';
import { fetchClients } from '../../services/clientApi';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ClientAutocomplete Component
 * Reusable autocomplete component for client selection with API integration
 * 
 * Features:
 * - Debounced API calls (300ms)
 * - Smart sorting (starts with search query first, then contains)
 * - Loads initial 50 clients on open
 * - Cancels outdated requests
 * - Loading states and error handling
 * 
 * @param {Object} props
 * @param {Object|null} props.value - Selected client object
 * @param {Function} props.onSelect - Callback when client is selected (receives client object or null)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {boolean} props.error - Whether to show error state
 * @param {string} props.helperText - Helper text to display
 * @param {boolean} props.required - Whether the field is required
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {string} props.size - Size of the input ('small' | 'medium')
 */
const ClientAutocomplete = ({
  value = null,
  onSelect,
  placeholder = 'Select Client',
  disabled = false,
  error = false,
  helperText = '',
  required = false,
  sx = {},
  size = 'small',
  ...otherProps
}) => {
  const { user } = useAuth();
  const cacheUserId = user?.employee_id || '';
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Fetch clients (GET /client?limit=50&search=…); uses localStorage via fetchClients
  const fetchClientsData = useCallback(async (searchQuery = '') => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setLoading(true);

    try {
      const response = await fetchClients(searchQuery, 50, { cacheUserId });

      if (response.success && Array.isArray(response.data)) {
        // Sort clients: first those starting with search query, then those containing it
        let sortedClients = [...response.data];

        if (searchQuery && searchQuery.trim()) {
          const query = searchQuery.trim().toLowerCase();
          sortedClients.sort((a, b) => {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();

            const aStartsWith = aName.startsWith(query);
            const bStartsWith = bName.startsWith(query);

            // If one starts with query and other doesn't, prioritize the one that starts
            if (aStartsWith && !bStartsWith) return -1;
            if (!aStartsWith && bStartsWith) return 1;

            // Both start with or both don't - sort alphabetically
            return aName.localeCompare(bName);
          });
        } else {
          // No search query - just sort alphabetically
          sortedClients.sort((a, b) => {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();
            return aName.localeCompare(bName);
          });
        }

        setOptions(sortedClients);
      } else {
        setOptions([]);
      }
    } catch (error) {
      // Ignore aborted requests
      if (error.name !== 'AbortError') {
        console.error('Error fetching clients:', error);
        setOptions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [cacheUserId]);

  // Load initial clients when component mounts
  useEffect(() => {
    fetchClientsData('');
  }, [fetchClientsData]); // Only on mount

  // Handle input change with debouncing
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced fetch
    debounceTimerRef.current = setTimeout(() => {
      if (inputValue !== undefined) {
        fetchClientsData(inputValue);
      }
    }, 300);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, fetchClientsData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle client selection
  const handleChange = (event, newValue) => {
    if (onSelect) {
      onSelect(newValue);
    }
  };

  // Handle input change
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.client_name || ''}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option._id || option.client_name}>
          {option.client_name}
        </Box>
      )}
      noOptionsText={loading ? 'Loading...' : 'No clients found'}
      filterOptions={(x) => x} // Disable local filtering, we're doing server-side search
      {...otherProps}
    />
  );
};

export default ClientAutocomplete;
